import React, { memo, useMemo } from 'react';
import { View, Text, TextInput, ScrollView, StyleSheet } from 'react-native';
import { TagPicker } from './TagPicker';
import { AttachmentPicker } from './AttachmentPicker';
import { CORK_BOARD_INPUT_LIMITS } from '@/constants/residents/corkBoardConstants';
import { SEMANTIC_COLORS, PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import type { CorkBoardTag, CorkBoardAttachment, LocalAttachment } from '@/types/residents/corkBoard';
import type { ThreadFormErrors } from '@/hooks/residents/corkBoard/useThreadForm';

interface CreateThreadFormProps {
  mode: 'create' | 'edit';
  tags: CorkBoardTag[];

  title: string;
  onChangeTitle: (text: string) => void;
  body: string;
  onChangeBody: (text: string) => void;
  selectedTagIds: number[];
  onToggleTag: (tagId: number) => void;

  attachments: LocalAttachment[];
  existingAttachments: CorkBoardAttachment[];
  onAddFiles: (files: LocalAttachment[]) => void;
  onRemoveFile: (index: number) => void;
  onRemoveExistingAttachment: (id: string) => void;

  errors: ThreadFormErrors;
  hasAttemptedSubmit: boolean;
  isSubmitting: boolean;
  uploadProgress?: number | null;
  submitError: string | null;
}

export const CreateThreadForm: React.FC<CreateThreadFormProps> = memo(({
  mode,
  tags,
  title,
  onChangeTitle,
  body,
  onChangeBody,
  selectedTagIds,
  onToggleTag,
  attachments,
  existingAttachments,
  onAddFiles,
  onRemoveFile,
  onRemoveExistingAttachment,
  errors,
  hasAttemptedSubmit,
  isSubmitting,
  uploadProgress,
  submitError,
}) => {
  const showErrors = hasAttemptedSubmit;

  const categoryTags = useMemo(
    () => tags.filter((t) => t.slug !== 'announcements'),
    [tags],
  );

  return (
    <ScrollView
      style={styles.scrollView}
      contentContainerStyle={styles.contentContainer}
      keyboardShouldPersistTaps="handled"
      showsVerticalScrollIndicator={false}
    >
      {submitError ? (
        <View style={styles.submitErrorBanner}>
          <Text style={styles.submitErrorText}>{submitError}</Text>
        </View>
      ) : null}

      {/* Tags */}
      <TagPicker
        tags={categoryTags}
        selectedTagIds={selectedTagIds}
        onToggleTag={onToggleTag}
        error={showErrors ? errors.tags : undefined}
      />

      {/* Title */}
      <View style={styles.fieldContainer}>
        <View style={styles.labelRow}>
          <Text style={styles.label}>Title</Text>
          <Text style={styles.charCounter}>
            {title.length}/{CORK_BOARD_INPUT_LIMITS.titleMax}
          </Text>
        </View>
        <TextInput
          style={[styles.textInput, showErrors && errors.title ? styles.inputError : undefined]}
          value={title}
          onChangeText={onChangeTitle}
          placeholder="Enter a title..."
          placeholderTextColor={SEMANTIC_COLORS.text.muted}
          maxLength={CORK_BOARD_INPUT_LIMITS.titleMax}
          editable={!isSubmitting}
          autoCapitalize="sentences"
          returnKeyType="next"
        />
        {showErrors && errors.title ? (
          <Text style={styles.errorText}>{errors.title}</Text>
        ) : null}
      </View>

      {/* Body */}
      <View style={styles.fieldContainer}>
        <View style={styles.labelRow}>
          <Text style={styles.label}>Description</Text>
          <Text style={styles.charCounter}>
            {body.length}/{CORK_BOARD_INPUT_LIMITS.bodyMax}
          </Text>
        </View>
        <TextInput
          style={[
            styles.textInput,
            styles.bodyInput,
            showErrors && errors.body ? styles.inputError : undefined,
          ]}
          value={body}
          onChangeText={onChangeBody}
          placeholder="Write your post..."
          placeholderTextColor={SEMANTIC_COLORS.text.muted}
          maxLength={CORK_BOARD_INPUT_LIMITS.bodyMax}
          editable={!isSubmitting}
          multiline
          textAlignVertical="top"
          autoCapitalize="sentences"
        />
        {showErrors && errors.body ? (
          <Text style={styles.errorText}>{errors.body}</Text>
        ) : null}
      </View>

      {mode === 'create' ? (
        <AttachmentPicker
          attachments={attachments}
          existingAttachments={existingAttachments}
          onAddFiles={onAddFiles}
          onRemoveFile={onRemoveFile}
          onRemoveExistingAttachment={onRemoveExistingAttachment}
          isSubmitting={isSubmitting}
          uploadProgress={uploadProgress}
          error={showErrors ? errors.files : undefined}
        />
      ) : existingAttachments.length > 0 ? (
        <AttachmentPicker
          attachments={[]}
          existingAttachments={existingAttachments}
          onAddFiles={() => {}}
          onRemoveFile={() => {}}
          readonly
        />
      ) : null}
    </ScrollView>
  );
});

CreateThreadForm.displayName = 'CreateThreadForm';

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  contentContainer: {
    padding: 16,
    paddingBottom: 40,
    gap: 20,
  },
  fieldContainer: {
    gap: 6,
  },
  labelRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: SEMANTIC_COLORS.text.primary,
  },
  charCounter: {
    fontSize: 12,
    color: SEMANTIC_COLORS.text.muted,
  },
  textInput: {
    backgroundColor: PRIMITIVE_COLORS.gray850,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: PRIMITIVE_COLORS.gray700,
    paddingHorizontal: 14,
    paddingVertical: 12,
    fontSize: 15,
    color: SEMANTIC_COLORS.text.primary,
  },
  bodyInput: {
    minHeight: 140,
    paddingTop: 12,
  },
  inputError: {
    borderColor: SEMANTIC_COLORS.interactive.danger,
  },
  errorText: {
    fontSize: 12,
    color: SEMANTIC_COLORS.interactive.danger,
  },
  submitErrorBanner: {
    backgroundColor: 'rgba(239, 68, 68, 0.12)',
    borderRadius: 8,
    padding: 12,
  },
  submitErrorText: {
    fontSize: 13,
    color: SEMANTIC_COLORS.interactive.danger,
    textAlign: 'center',
  },
});
